import LinkifyIt from 'linkify-it'
import { FILE_EXTENSIONS_PATTERN } from '../../lib/file-classification'

/**
 * Linkify - URL and file path detection for markdown preprocessing
 *
 * Uses linkify-it (12M downloads/week) for battle-tested URL detection,
 * plus custom regex for local file paths.
 */

// Initialize linkify-it with default settings (fuzzy URLs, emails enabled)
const linkify = new LinkifyIt()

// File path regex - detects absolute/home/explicit-relative/bare-relative paths with common extensions.
// Examples: /Users/foo.ts, ~/src/app.tsx, ./README.md, ../guide.md, apps/electron/src/main.ts,
// C:\Users\me\notes.md, file:///C:/Users/me/image.png
// Extensions derived from file-classification.ts to stay in sync with preview support
const FILE_EXTENSION_SOURCE = `\\.(?:${FILE_EXTENSIONS_PATTERN})(?:[?#][^\\s)\\]}\\.,:;!?>]*)?`
const FILE_PATH_BODY_SOURCE = [
  `file://[^\\s"'\\\`<>]+?${FILE_EXTENSION_SOURCE}`,
  `[A-Za-z]:[/\\\\][^\\s"'\\\`<>]*${FILE_EXTENSION_SOURCE}`,
  `\\\\\\\\[^\\s"'\\\`<>]+(?:[/\\\\][^\\s"'\\\`<>]+)+${FILE_EXTENSION_SOURCE}`,
  `[^\\s"'\\\`<>]+${FILE_EXTENSION_SOURCE}`,
  `(?:/|~/|\\./|\\.\\./|[A-Za-z0-9_][\\w\\-./@%\\\\]*)[\\w\\-./@%\\\\]*${FILE_EXTENSION_SOURCE}`,
].join('|')
const FILE_PATH_REGEX_SOURCE = `(?:^|[\\s([\\{<])(${FILE_PATH_BODY_SOURCE})(?=[\\s)\\]}\\.,:;!?>]|$)`
const FILE_PATH_REGEX = new RegExp(FILE_PATH_REGEX_SOURCE, 'gi')
const FILE_PATH_PRETEST_REGEX = new RegExp(FILE_PATH_REGEX_SOURCE, 'i')

const FILE_LINE_SUFFIX_SOURCE = `:\\d+(?::\\d+)?(?:-\\d+(?::\\d+)?)?`
const FILE_EXTENSION_TARGET_REGEX = new RegExp(`${FILE_EXTENSION_SOURCE}$`, 'i')
const FILE_LINE_SUFFIX_REGEX = new RegExp(
  `^(.+\\.(?:${FILE_EXTENSIONS_PATTERN})(?:[?#][^\\s)\\]}\\.,:;!?>]*)?)(?:${FILE_LINE_SUFFIX_SOURCE})$`,
  'i'
)
const BARE_RELATIVE_FILE_TARGET_REGEX = new RegExp(
  `^[^\\s"'\\\`<>]+\\.(?:${FILE_EXTENSIONS_PATTERN})(?:[?#].*)?$`,
  'i'
)
const SPACED_BARE_RELATIVE_FILE_TARGET_REGEX = new RegExp(
  `^[^\\r\\n"'\\\`<>\\[\\]/\\\\]+\\.(?:${FILE_EXTENSIONS_PATTERN})(?:[?#].*)?$`,
  'i'
)
const BARE_FILE_LINE_REGEX = new RegExp(
  `^(\\s*(?:[-*+]\\s+|\\d+[.)]\\s+)?)`
  + `([^\\r\\n"'\\\`<>\\[\\]/\\\\]+\\.(?:${FILE_EXTENSIONS_PATTERN})(?:[?#][^\\s)\\]}\\.,:;!?>]*)?(?:${FILE_LINE_SUFFIX_SOURCE})?)`
  + `\\s*$`,
  'i'
)
// Labels used to present an artifact are not part of its filename. Keep this
// deliberately narrow instead of splitting at every colon: full-width colons
// and spaces are both valid in POSIX filenames (for example, `客户：华东 周报.xlsx`).
const BARE_FILE_PRESENTATION_PREFIX_REGEX = /^(\s*(?:点击\s*)?(?:下载(?:文件|链接)?|download(?:\s+(?:file|link))?)\s*[:：]\s*)(.+)$/i
const NON_FILE_SCHEME_REGEX = /^(?:https?|mailto|ftp|data):/i
const SAFE_RELATIVE_MARKDOWN_TARGET_REGEX = /^[^\s()[\]<>`]+$/
const TRAILING_FILE_LINE_SUFFIX_REGEX = new RegExp(`^(?:${FILE_LINE_SUFFIX_SOURCE})`)
const FILE_LINK_BOUNDARY_REGEX = /^[\s)\]}.,;!?>]$/

function safeDecodeURIComponent(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

export function normalizeFilePathTarget(target: string): string {
  const trimmed = target.trim()
  if (!trimmed) return ''
  const unwrapped = unwrapMarkdownLinkDestination(trimmed)
  const withoutLineSuffix = stripFileLineSuffix(unwrapped)
  if (/^(?:https?:|mailto:|ftp:|data:|file:)/i.test(withoutLineSuffix)) return withoutLineSuffix
  return safeDecodeURIComponent(withoutLineSuffix)
}

interface DetectedLink {
  type: 'url' | 'email' | 'file'
  text: string
  url: string
  start: number
  end: number
}

interface CodeRange {
  start: number
  end: number
}

/**
 * Find all code block and inline code ranges in text
 * These ranges should be excluded from link detection
 */
function findCodeRanges(text: string): CodeRange[] {
  const ranges: CodeRange[] = []

  // Find fenced code blocks (```...```)
  const fencedRegex = /```[\s\S]*?```/g
  let match
  while ((match = fencedRegex.exec(text)) !== null) {
    ranges.push({ start: match.index, end: match.index + match[0].length })
  }

  // During streaming, fenced code blocks are often temporarily unclosed.
  // Treat the opening fence through EOF as code so linkification does not
  // rewrite plain file lists before the closing fence arrives.
  let searchStart = 0
  while (searchStart < text.length) {
    const fenceStart = text.indexOf('```', searchStart)
    if (fenceStart === -1) break
    const insideClosedFence = ranges.some(r => fenceStart >= r.start && fenceStart < r.end)
    if (insideClosedFence) {
      const range = ranges.find(r => fenceStart >= r.start && fenceStart < r.end)
      searchStart = range ? range.end : fenceStart + 3
      continue
    }
    ranges.push({ start: fenceStart, end: text.length })
    break
  }

  // Find inline code (`...`)
  // But skip escaped backticks and code inside fenced blocks
  const inlineRegex = /(?<!`)`(?!`)([^`\n]+)`(?!`)/g
  while ((match = inlineRegex.exec(text)) !== null) {
    const pos = match.index
    // Check if this is inside a fenced block
    const insideFenced = ranges.some(r => pos >= r.start && pos < r.end)
    if (!insideFenced) {
      ranges.push({ start: pos, end: pos + match[0].length })
    }
  }

  return ranges
}

/**
 * Check if a position is inside any code range
 */
function isInsideCode(pos: number, ranges: CodeRange[]): boolean {
  return ranges.some(r => pos >= r.start && pos < r.end)
}

/**
 * Find all markdown link ranges in text: both [text](...) and [text][ref] patterns.
 * Returns ranges covering the entire link syntax so any URL detected within
 * these spans is skipped by preprocessLinks() — preventing nested/broken links.
 */
function findMarkdownLinkRanges(text: string): CodeRange[] {
  const ranges: CodeRange[] = []

  // Match [text](url) — inline links
  const inlineLinkRegex = /\[((?:[^\[\]\\]|\\.)*)\]\(((?:<[^>\n]*>|\\.|[^()\\\n]|\([^()]*\))*)\)/g
  let match
  while ((match = inlineLinkRegex.exec(text)) !== null) {
    ranges.push({ start: match.index, end: match.index + match[0].length })
  }

  // Match [text][ref] — reference links
  const refLinkRegex = /\[(?:[^\[\]]|\\\[|\\\])*\]\[[^\]]*\]/g
  while ((match = refLinkRegex.exec(text)) !== null) {
    // Avoid duplicates with inline links that already matched
    const r = { start: match.index, end: match.index + match[0].length }
    const alreadyCovered = ranges.some(existing => rangesOverlap(existing, r))
    if (!alreadyCovered) {
      ranges.push(r)
    }
  }

  return ranges
}

/**
 * Check if ranges overlap
 */
function rangesOverlap(a: { start: number; end: number }, b: { start: number; end: number }): boolean {
  return a.start < b.end && b.start < a.end
}

function getTrailingFileLineSuffix(text: string, offset: number): string {
  const match = text.slice(offset).match(TRAILING_FILE_LINE_SUFFIX_REGEX)
  if (!match?.[0]) return ''

  const next = text[offset + match[0].length]
  if (next && !FILE_LINK_BOUNDARY_REGEX.test(next)) return ''
  return match[0]
}

function looksLikeBareFileListingPath(path: string, prefix: string): boolean {
  if (!/\s/.test(path)) return true
  if (/[-*+]|\d+[.)]/.test(prefix)) return true
  // CJK names commonly contain spaces, but an ASCII `Label: prose file.ext`
  // sentence should not be mistaken for one filename. Full-width-colon names
  // remain supported (the common Chinese filename convention).
  if (/[\u3400-\u9fff]/.test(path) && (!/:/.test(path) || /：/.test(path))) return true
  if (/\b(?:19|20)\d{2}[-_年]\d{1,2}/.test(path)) return true
  if (/[（(][^）)]*[）)]\.[^.\s]+(?:[?#].*)?$/i.test(path)) return true
  return /^[\w.-]*\d[\w.-]*\s/.test(path)
}

interface FilePresentationSplit {
  prefix: string
  path: string
}

function splitFilePresentationPrefix(path: string): FilePresentationSplit {
  const match = path.match(BARE_FILE_PRESENTATION_PREFIX_REGEX)
  if (!match?.[2]) return { prefix: '', path }
  return { prefix: match[1] ?? '', path: match[2] }
}

function detectBareFileLineLinks(text: string): DetectedLink[] {
  const links: DetectedLink[] = []
  let lineStart = 0

  while (lineStart <= text.length) {
    const newlineIndex = text.indexOf('\n', lineStart)
    const lineEnd = newlineIndex === -1 ? text.length : newlineIndex
    const line = text.slice(lineStart, lineEnd)
    const match = line.match(BARE_FILE_LINE_REGEX)

    if (match?.[2]) {
      const listPrefix = match[1] ?? ''
      const presentation = splitFilePresentationPrefix(match[2])
      const presentationPrefix = presentation.prefix
      const path = presentation.path

      if (!looksLikeBareFileListingPath(path, listPrefix)) {
        if (newlineIndex === -1) break
        lineStart = newlineIndex + 1
        continue
      }

      const fileTarget = stripFileLineSuffix(path)
      const start = lineStart + listPrefix.length + presentationPrefix.length
      links.push({
        type: 'file',
        text: fileTarget,
        url: fileTarget,
        start,
        end: start + path.length,
      })
    }

    if (newlineIndex === -1) break
    lineStart = newlineIndex + 1
  }

  return links
}

/**
 * Detect all links (URLs, emails, file paths) in text
 */
export function detectLinks(text: string): DetectedLink[] {
  const links: DetectedLink[] = []
  const fileLinks: DetectedLink[] = detectBareFileLineLinks(text)

  // Pre-compute markdown link spans so the greedy FILE_PATH_REGEX can't claim
  // a span that crosses an existing `[text](url)` boundary. Without this guard,
  // input like `技能文件：[SKILL.md](file:///…/SKILL.md)` matched as a single
  // path from `技能文件：[SKILL.md](file:` through the trailing `.md`, and
  // preprocessLinks then re-wrapped the original markdown link into a broken
  // nested one.
  const markdownLinkRanges = findMarkdownLinkRanges(text)

  FILE_PATH_REGEX.lastIndex = 0
  let fileMatch
  while ((fileMatch = FILE_PATH_REGEX.exec(text)) !== null) {
    const rawPath = fileMatch[1]
    if (!rawPath) continue
    if (NON_FILE_SCHEME_REGEX.test(rawPath)) continue

    const presentation = splitFilePresentationPrefix(rawPath)
    const path = presentation.path

    // The broad bare-relative branch is intentionally permissive for names
    // with spaces. Apply the same listing heuristic here so ordinary prose
    // such as `错误: install package.json` cannot become one giant href.
    if (!/[\\/]/.test(path) && /\s/.test(path) && !looksLikeBareFileListingPath(path, '')) {
      continue
    }

    const fullMatch = fileMatch[0]
    const pathOffset = fullMatch.indexOf(path)
    // pathOffset already points at the post-label filename inside fullMatch;
    // do not add presentation.prefix again (that would skip the first three
    // characters of the filename when trailing prose follows the file).
    const start = fileMatch.index + pathOffset
    const lineSuffix = getTrailingFileLineSuffix(text, start + path.length)
    const textEnd = start + path.length + lineSuffix.length
    const link = {
      start,
      end: textEnd,
    }
    if (markdownLinkRanges.some(existing => rangesOverlap(existing, link))) continue
    if (fileLinks.some(existing => rangesOverlap(existing, link))) continue

    fileLinks.push({
      type: 'file',
      text: path,
      url: path,
      start,
      end: textEnd
    })
  }

  // 1. Detect URLs and emails with linkify-it
  const urlMatches = linkify.match(text) || []
  // linkify-it doesn't strip trailing asterisks from bold/italic markdown,
  // which causes broken links when URLs are wrapped like **url** or *url*
  // Note: _ and ~ are valid URL chars so we only strip *
  const trailingMarkdownRe = /\*+$/
  for (const match of urlMatches) {
    let matchText = match.text
    let matchUrl = match.url
    let matchEnd = match.lastIndex

    const stripped = matchText.replace(trailingMarkdownRe, '')
    if (stripped !== matchText) {
      const diff = matchText.length - stripped.length
      matchText = stripped
      matchUrl = matchUrl.replace(trailingMarkdownRe, '')
      matchEnd -= diff
    }

    const urlRange = { start: match.index, end: matchEnd }
    const overlapsFile = fileLinks.some(link => rangesOverlap(urlRange, link))
    if (overlapsFile) continue

    links.push({
      type: match.schema === 'mailto:' ? 'email' : 'url',
      text: matchText,
      url: matchUrl,
      start: match.index,
      end: matchEnd
    })
  }

  links.push(...fileLinks)

  // Sort by position
  return links.sort((a, b) => a.start - b.start)
}

/**
 * Detect placeholder/fabricated URLs that the AI generated without knowing the real URL.
 * These are URLs like `https://github.com/...` or `https://example.com/...`
 * that should be stripped back to inline code instead of rendered as links.
 */
const PLACEHOLDER_URL_PATTERN = /\/\.\.\.(?:[)/\s#?]|$)/

/**
 * Check if a URL looks like a placeholder/fabricated URL.
 * Returns true for URLs containing path segments like `/...`
 */
export function isPlaceholderUrl(url: string): boolean {
  return PLACEHOLDER_URL_PATTERN.test(url)
}

/**
 * Strip markdown links with placeholder URLs back to plain text.
 * Converts `[text](https://github.com/...)` → `text`
 * Respects code blocks — links inside fenced or inline code are not touched.
 */
function stripPlaceholderLinks(text: string): string {
  const codeRanges = findCodeRanges(text)
  // Match markdown links [text](url) where url contains placeholder patterns
  return text.replace(
    /\[((?:[^\[\]\\]|\\.)*)\]\(((?:<[^>\n]*>|\\.|[^()\\\n]|\([^()]*\))*)\)/g,
    (fullMatch, linkText: string, url: string, offset: number) => {
      // Don't modify links inside code blocks
      if (isInsideCode(offset, codeRanges)) return fullMatch

      if (isPlaceholderUrl(unwrapMarkdownLinkDestination(url))) {
        // Strip the link, keep just the display text as plain text
        if (!linkText.trim()) return fullMatch
        return linkText
      }
      return fullMatch
    }
  )
}

function unwrapMarkdownLinkDestination(target: string): string {
  const trimmed = target.trim()
  if (trimmed.startsWith('<') && trimmed.endsWith('>')) {
    return trimmed.slice(1, -1).trim()
  }
  return trimmed
}

export function stripFileLineSuffix(target: string): string {
  const match = target.trim().match(FILE_LINE_SUFFIX_REGEX)
  return match?.[1] ?? target
}

function encodeFileUrlPath(path: string): string {
  return path
    .split('/')
    .map((segment, index) => {
      if (index === 0 && /^[A-Za-z]:$/.test(segment)) return segment
      return encodeURIComponent(segment)
    })
    .join('/')
}

/**
 * Encode a file:// target without turning existing percent escapes into
 * literal `%25` sequences. `normalizeLocalMarkdownLinkTargets` can run more
 * than once while a response streams, so this function must be idempotent.
 */
function encodeFileUrlPreservingEscapes(uri: string): string {
  return uri
    .split(/(%[0-9a-f]{2})/gi)
    .map(part => /^%[0-9a-f]{2}$/i.test(part) ? part : encodeURI(part))
    .join('')
}

function toSafeFileLinkTarget(target: string): string {
  const unwrapped = stripFileLineSuffix(unwrapMarkdownLinkDestination(target))

  if (/^file:\/\//i.test(unwrapped)) {
    return encodeFileUrlPreservingEscapes(unwrapped)
  }

  if (/^[A-Za-z]:[\\/]/.test(unwrapped)) {
    return `file:///${encodeFileUrlPath(unwrapped.replace(/\\/g, '/'))}`
  }

  const uncMatch = unwrapped.match(/^\\\\([^\\/:*?"<>|]+)\\(.+)$/)
  if (uncMatch?.[1] && uncMatch?.[2]) {
    return `file://${encodeURIComponent(uncMatch[1])}/${encodeFileUrlPath(uncMatch[2].replace(/\\/g, '/'))}`
  }

  if (unwrapped.startsWith('/')) {
    return `file://${encodeFileUrlPath(unwrapped)}`
  }

  if (SAFE_RELATIVE_MARKDOWN_TARGET_REGEX.test(unwrapped)) return unwrapped

  return `<${unwrapped.replace(/>/g, '%3E')}>`
}

/**
 * Normalize existing markdown links whose target is a local file path.
 *
 * Raw Windows paths such as `[Open](C:\Users\me\OKClaw Pro\file.html)` are not
 * valid markdown destinations because of spaces and backslashes, so micromark
 * renders the whole `[text](path)` as plain text. Convert them to safe file URLs
 * before parsing while preserving the user-facing link text.
 */
function normalizeLocalMarkdownLinkTargets(text: string): string {
  const codeRanges = findCodeRanges(text)

  return text.replace(
    /\[((?:[^\[\]\\]|\\.)*)\]\(((?:<[^>\n]*>|\\.|[^()\\\n]|\([^()]*\))*)\)/g,
    (fullMatch, linkText: string, target: string, offset: number) => {
      if (isInsideCode(offset, codeRanges)) return fullMatch

      const unwrapped = unwrapMarkdownLinkDestination(target)
      if (!isFilePathTarget(unwrapped)) return fullMatch

      return `[${linkText}](${toSafeFileLinkTarget(unwrapped)})`
    }
  )
}

/**
 * Preprocess text to convert raw URLs and file paths into markdown links
 * Skips code blocks and already-linked content
 */
export function preprocessLinks(text: string): string {
  // First, fix existing markdown links whose href is a local filesystem path
  // that would otherwise be invalid markdown (notably Windows paths with spaces).
  text = normalizeLocalMarkdownLinkTargets(text)

  // First pass: strip markdown links with placeholder/fabricated URLs
  // (e.g., AI-generated `[commit](https://github.com/...)` → `\`commit\``)
  text = stripPlaceholderLinks(text)

  // Quick check - if no potential links, return early
  if (!linkify.pretest(text) && !FILE_PATH_PRETEST_REGEX.test(text)) {
    return text
  }

  const codeRanges = findCodeRanges(text)
  const markdownLinkRanges = findMarkdownLinkRanges(text)
  const links = detectLinks(text)

  if (links.length === 0) return text

  // Build result, converting raw links to markdown links
  let result = ''
  let lastIndex = 0

  for (const link of links) {
    // Skip if inside code block
    if (isInsideCode(link.start, codeRanges)) continue

    // Skip if it touches an existing markdown link anywhere — `start` alone
    // isn't enough because a greedy file-path match can start before the `[`
    // and stretch past the `)`.
    if (markdownLinkRanges.some(r => rangesOverlap(r, link))) continue

    // Add text before this link
    result += text.slice(lastIndex, link.start)

    // Convert to markdown link
    const href = link.type === 'file' ? toSafeFileLinkTarget(link.url) : link.url
    result += `[${link.text}](${href})`

    lastIndex = link.end
  }

  // Add remaining text
  result += text.slice(lastIndex)

  return result
}

/**
 * Test if text contains any detectable links
 * Useful for optimization - skip preprocessing if no links present
 */
export function hasLinks(text: string): boolean {
  return linkify.pretest(text) || FILE_PATH_PRETEST_REGEX.test(text)
}

/**
 * Check whether a markdown anchor target should be treated as a local file path.
 * Used by click handlers to route local paths to onFileClick instead of onUrlClick.
 */
export function isFilePathTarget(target: string): boolean {
  const trimmed = normalizeFilePathTarget(unwrapMarkdownLinkDestination(target))
  if (!trimmed) return false
  if (NON_FILE_SCHEME_REGEX.test(trimmed)) return false

  // `sandbox:` is a renderer-visible alias for the current session folder.
  // Only accept it when its payload is a local path; do not turn an arbitrary
  // `sandbox:https://...` string into a file click.
  const sandboxPayload = trimmed.match(/^sandbox:(.*)$/i)?.[1]?.trim()
  if (sandboxPayload !== undefined) {
    if (!sandboxPayload || /^(?:https?|mailto|ftp|data):/i.test(sandboxPayload)) return false
    return true
  }

  // The placeholder is expanded by path-resolution once the session folder
  // is available. It is still an explicit local target for link dispatch.
  if (trimmed.includes('{{SESSION_PATH}}')) return true

  // file:// URLs and absolute paths can point to directories (no extension).
  // Anything else needs a known extension to avoid wrapping random text.
  const isExplicitLocalTarget = /^file:\/\//i.test(trimmed)
    || /^[A-Za-z]:[\\/]/.test(trimmed)
    || /^\\\\[^\\/]+[\\/][^\\/]+/.test(trimmed)
    || /^(?:\/|~\/)/.test(trimmed)
  if (isExplicitLocalTarget) return true

  if (!FILE_EXTENSION_TARGET_REGEX.test(trimmed)) return false

  return /^(?:\.\/|\.\.\/)/.test(trimmed)
    || /^[A-Za-z0-9_][^\r\n]*[\\/][^\r\n]*$/.test(trimmed)
    || BARE_RELATIVE_FILE_TARGET_REGEX.test(trimmed)
    || SPACED_BARE_RELATIVE_FILE_TARGET_REGEX.test(trimmed)
}
